/**
 * PDF download + failure-message helpers.
 *
 * Extracted from main.ts so they can be unit-tested: main.ts is the browser
 * entry point — it imports CSS, mounts into #app, and throws if that element
 * is missing — so importing it from a test to reach these three functions is
 * not workable. Nothing here touches app state; they are pure functions plus
 * one DOM side effect.
 */

/**
 * Translate raw error to something the DC can act on. Most-common cases get
 * a recognizable phrase; unknown errors fall through to the message verbatim.
 */
export function friendlyPdfError(err: unknown): string {
  if (!(err instanceof Error)) return "Unknown error — try refreshing the page.";
  const raw = err.message;
  // The lazy PDF chunk itself failed to load. Almost always a tab opened before
  // a deploy: the old chunk's hashed filename is gone, Cloudflare Pages answers
  // with index.html, and the module import fails. Wording per engine: Chrome
  // "Failed to fetch dynamically imported module", Firefox "error loading
  // dynamically imported module", Safari "Importing a module script failed".
  // Checked before the network branch, which "Failed to fetch" would also hit.
  if (/dynamically imported module|importing a module script failed/i.test(raw)) {
    return "Couldn't load the PDF tool. The calculator was likely updated since this page was opened. Refresh the page, then calculate again.";
  }
  // Browser fetch failures surface as "Failed to fetch" or net::ERR_*.
  if (/failed to fetch|networkerror|err_/i.test(raw)) {
    return "Couldn't load PDF assets. Check your connection and try again.";
  }
  // CSP / cross-origin denial.
  if (/refused to|blocked by|csp/i.test(raw)) {
    return "Browser blocked a required asset. Try a different browser or disable any blocking extensions.";
  }
  // Out-of-memory or quota errors on weak devices.
  if (/quota|memory|out of/i.test(raw)) {
    return "Ran out of memory generating the PDF. Try a different device or browser tab.";
  }
  return raw;
}

/**
 * Programmatically trigger a browser download of `bytes` as `filename`.
 * Anchor + click + revoke is the most-compatible pattern. iOS Safari is
 * pickier than desktop browsers and prefers the element to be in the DOM
 * before the click — we add and remove explicitly.
 */
export function triggerDownload(bytes: Uint8Array, filename: string): void {
  const blob = new Blob([bytes], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke after a tick so the browser has time to start the download.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** Build a filesystem-safe filename: `head-shape-<baby>-<YYYY-MM-DD>.pdf`. */
export function filenameFor(babyName: string): string {
  const slug = babyName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  const datePart = `${yyyy}-${mm}-${dd}`;
  return slug === "" ? `head-shape-${datePart}.pdf` : `head-shape-${slug}-${datePart}.pdf`;
}
